import type {UserDetails} from 'src/types/referrer-portal';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch, useSelector} from 'react-redux';
import {useInflight} from 'src/hooks/redux';
import * as reduxApi from 'src/utils/redux-api-referrer-portal';
import {selectUserDetails} from 'src/selectors/referrer-portal';
import {userFormInitialValue} from 'src/utils/referrer-portal';

import {getUserDetails} from 'src/action-creators/referrer-portal/authentication';

import {
  CircularLoader,
  Card,
  SubTitleMedium,
  BodySmall,
  Avatar,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/index';
import {toastMessage} from 'src/components/referral-v2/tracking/referral-confirm-modal';
import ViewProfile from './view-profile';
import EditProfile from './edit-profile';
import css from 'src/components/referrer-portal/settings/settings.css';


const Profile = () => {
  const dispatch = useDispatch();
  const {t} = useLabelTranslation();

  const [isEdit, setIsEdit] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const account: UserDetails | null | undefined =
    useSelector(selectUserDetails);

  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const isUserDataLoading = useInflight(getUserDetails());

  const onChangeEdit = () => setIsEdit(!isEdit);

  const handleSubmit = (data: UserDetails) => {
    setIsSubmitting(true);
    dispatch(reduxApi.patch('user', {data}))
      .then(() => {
        setIsSubmitting(false);
        onChangeEdit();
        toastApi.show(
          toastMessage({
            message: t(
              'Details_updated_successfully',
              'Details updated successfully',
            ),
          }),
        );
        // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
        dispatch(getUserDetails());
      })
      .catch(() => {
        toastApi.show(
          toastMessage({
            message: t('User_details_not_updated', 'User details not updated'),
            semantic: 'danger',
          }),
        );
        setIsSubmitting(false);
      });
  };

  React.useEffect(() => {
    if (!account) {
      // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
      dispatch(getUserDetails());
    }
  }, []);

  if (isUserDataLoading) {
    return <CircularLoader className={css.loader} />;
  }

  return (
    <div className={css.profilePage}>
      {isEdit ? (
        <EditProfile
          userData={account}
          handleSubmit={handleSubmit}
          onChangeEdit={onChangeEdit}
          isSubmitting={isSubmitting}
        />
      ) : (
        <ViewProfile
          userData={account ?? userFormInitialValue}
          onChangeEdit={onChangeEdit}
        />
      )}
      <ProfileCard userData={account ?? userFormInitialValue} />
    </div>
  );
};

export default Profile;

const ProfileCard = ({userData}: {userData: UserDetails}) => {
  const [profileUrl, setProfileUrl] = React.useState<any>(null);

  const dispatch = useDispatch();

  React.useEffect(() => {
    if (userData.profile_picture_url) {
      dispatch(reduxApi.get('user/picture')).then((data) =>
        setProfileUrl(data),
      );
    }
  }, [userData.profile_picture_url]);

  return (
    <Card classNames={{wrapper: css.userCard}}>
      <Avatar
        text={userData.name}
        iconType="regular"
        imageSrc={profileUrl || ''}
        size="extraLarge"
      />
      <SubTitleMedium className={css.defaultMarginTop}>
        {userData.name}
      </SubTitleMedium>
      <BodySmall className={css.defaultMarginTop}>
        {userData.position ?? userData.position}{' '}
        {userData.company_name ? `@ ${userData.company_name}` : ''}{' '}
        {userData.location ? `| ${userData.location}` : ''}
      </BodySmall>
      {userData.bio && (
        // @ts-expect-error - TS2769 - No overload matches this call.
        <Card classNames={{wrapper: css.bio}} semantic="primary">
          <BodySmall>"{userData.bio}"</BodySmall>
        </Card>
      )}
    </Card>
  );
};
